import React from "react";
import { motion } from "framer-motion";
import {
  LinkIcon,
  VideoCameraIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import PropTypes from "prop-types";

const ProcessStep = ({ icon, title, description, isLast = false }) => {
  return (
    <div className="flex flex-col items-center text-center max-w-xs mx-auto">
      <div className="bg-gradient-to-br from-peachYellow to-coralRed p-4 rounded-full mb-4 shadow-md">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-2 text-ashGray">{title}</h3>
      <p className="text-ashGray">{description}</p>
    </div>
  );
};

const OurProcess = ({ ref2, controls2 }) => {
  return (
    <section
      id="our-process"
      className="py-20 px-4 bg-white relative overflow-hidden"
      ref={ref2}
    >
      {/* Background gradient circles */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-32 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-peachYellow/40 to-salmonPink/40 blur-[100px] animate-pulse"></div>
        <div
          className="absolute -bottom-32 right-32 w-[700px] h-[700px] rounded-full bg-gradient-to-tl from-coralRed/40 to-roseBrown/40 blur-[100px] animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute top-32 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-tr from-salmonPink/30 to-peachYellow/30 blur-[100px] animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      {/* Top gradient fade */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-white to-transparent z-0"></div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent z-0"></div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={controls2}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-ashGray font-sans">
            How It Works
          </h2>
          <div className="w-24 h-1 bg-gradient-primary mx-auto mb-6 rounded-full"></div>
          <p className="text-lg text-ashGray max-w-3xl mx-auto">
            Say goodbye to scheduling headaches and AI-generated applications
            with our simple three-step solution
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={controls2}
          transition={{ delay: 0.2 }}
          className="flex flex-col md:flex-row items-start justify-center gap-4 md:gap-0"
        >
          <ProcessStep
            icon={<LinkIcon className="h-8 w-8 text-white" />}
            title="Create & Share"
            description="Receive a link with a custom interview experience tailored to your business needs. Share it directly with candidates or post it on your job listings."
          />

          <ArrowRightIcon className="hidden md:block md:h-24 md:w-24 lg:h-16 lg:w-16 text-roseBrown mx-4" />

          <ProcessStep
            icon={<VideoCameraIcon className="h-8 w-8 text-white" />}
            title="Candidates Respond"
            description="Applicants record video responses to your questions at their convenience, building a digital profile for your review."
          />

          <ArrowRightIcon className="hidden md:block md:h-24 md:w-24 lg:h-16 lg:w-16 text-roseBrown mx-4" />

          <ProcessStep
            icon={<ClipboardDocumentCheckIcon className="h-8 w-8 text-white" />}
            title="Review & Select"
            description="Evaluate candidates on your own time and confidently select the best fit to advance in your hiring process."
            isLast={true}
          />
        </motion.div>
      </div>
    </section>
  );
};

ProcessStep.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isLast: PropTypes.bool,
};

OurProcess.propTypes = {
  ref2: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  controls2: PropTypes.object || PropTypes.func,
};

export default OurProcess;
